import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import * as Yup from 'yup';
import {
   Grid,
   GridItem,
   useColorModeValue,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Button,
   useToast,
   useColorMode,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { addSeries, getSeries, updateSeries } from '../../../../../redux/actions/series';
import { getBrand, getSingleBrand } from '../../../../../redux/actions/brand';
import { renderFormControl, renderSelectFormControl } from 'utils/DynamicFormData';
import '../../../companyProfile/css.css';
import { DynamicInput } from 'components/UIcomponents/Dynamicinput';
import { renderDynamicDropdown } from 'components/UIcomponents/DynamicDropdown';
import { renderDynamicDropdown as renderDynamicUiDropdown } from '../../../../../components/ui/DynamicDropdown';
import { getShareCompanyOptions } from '../../../../../utils/util';
import { useGlobal, useUser } from '../../../../../contexts';

// Yup Validation Schema
const validationSchema = Yup.object().shape({
   name: Yup.string().required('Name is required').min(1, 'Name must be at least 1 characters long'),

   ordinal: Yup.number().required('Ordinal is required').positive('Please select a valid ordinal'),

   brandid: Yup.number().required('Brand is required').positive('Please select a valid brand'),
});


const SeriesMasterForm = ({
   isOpen,
   onClose,
   isEditMode = false,
   demoname,
   currentRow = null,
   saveandcontinue = true,
}) => {
   const [row, setRow] = useState({
      name: '',
      ordinal: '',
      brandid: '',
      compIds: [],
   });
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const [errors, setErrors] = useState({});
   // const [activeStep, setActiveStep] = useState(0);
   const toast = useToast();
   const textColor = useColorModeValue('#9E9E9E', 'white');
   const [currentPage, setCurrentPage] = useState(1);
   const [isLoading, setIsLoading] = useState(false);
   const [limit, setLimit] = useState(10);
   const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
   const [sorting, setSorting] = React.useState([]);
   const dispatch = useDispatch();
   const brandData = useSelector((state) => state.brand.list);
   const { companyList, userGlobalSettingsData, company } = useGlobal();
   // const brandSingleData = useSelector((state) => state.brand.single || null);
   const seriesData = useSelector((state) => state.series.list || []);
   const { user } = useUser();

   const shouldSendCompanyidBrand = userGlobalSettingsData?.[0]?.askCompany?.brand || false;
   const shouldSendCompanyidSeries = userGlobalSettingsData?.[0]?.askCompany?.series || false;

   const getNextOrdinalValue = () => {
      if (!seriesData || seriesData.length === 0) return 1;

      // Find the highest ordinal value
      const highestOrdinal = Math.max(...seriesData.map((cat) => Number(cat.ordinal) || 0));

      // Return the next value
      return highestOrdinal + 1;
   };

   // Function to clear form data
   const clearFormData = () => {
      setRow({
         name: '',
         ordinal: '',
         brandid: '',
         compIds: [],
      });
      setErrors({});
   };

   useEffect(() => {
      if (isOpen) {
         dispatch(getBrand({ ...(shouldSendCompanyidBrand && { companyIds: user?.loginhistory?.companyId }) }));
      }
   }, [dispatch, isOpen]);


   useEffect(() => {
      if (!isEditMode) {
         // For new category, use the next ordinal value
         setRow({
            name: '',
            ordinal: Number(getNextOrdinalValue()),
            brandid: '',
            compIds: [],
         });
      } else if (isEditMode && currentRow) {
         // For editing, use the current values
         setRow({
            name: currentRow.name || '',
            ordinal: currentRow.ordinal || '',
            brandid: currentRow.brandid || '',
            compIds: currentRow.compIds || [],
         });
      }
   }, [isEditMode, currentRow, seriesData]);

   const validateStep = async () => {
      try {
         // Validate the whole row object instead of individual fields
         await validationSchema.validate(row, { abortEarly: false });

         setErrors({}); // Clear previous errors
         return true;
      } catch (err) {
         if (err instanceof Yup.ValidationError) {
            const errorObj = {};
            err.inner.forEach((error) => {
               errorObj[error.path] = error.message;
            });
            setErrors(errorObj);
            return false;
         }
         return false;
      }
   };

   const handleInputChange = (field, value) => {
      if (field === 'brandid' || field === 'ordinal') {
         // Only convert to number if value is not empty
         value = value ? Number(value) : '';
      }

      setRow((prev) => ({
         ...prev,
         [field]: value,
      }));

      // Clear the specific field error when user starts typing
      setErrors((prevErrors) => {
         const newErrors = { ...prevErrors };
         if (newErrors[field]) {
            delete newErrors[field];
         }
         return newErrors;
      });
   };

   const onFormSubmit = async () => {
      setIsLoading(true);
      const processedData = {
         ...row,
         ordinal: Number(row.ordinal),
         brandid: Number(row.brandid),
         // ratepersalesid: Number(row.ratepersalesid),
         // rateperpurchaseid: Number(row.rateperpurchaseid)
      };

      try {
         if (isEditMode && currentRow && currentRow.id) {
            // Update existing item
            processedData.id = currentRow.id;
            await dispatch(updateSeries(processedData));
         } else {
            // Add new item
            await dispatch(addSeries(processedData));
         }
         dispatch(
            getSeries({
               page: currentPage,
               // limit: limit === 'ALL' ? undefined : limit,
               search: debouncedSearchQuery,
               sortBy: sorting.length ? sorting[0]?.name : 'name',
               sortOrder: sorting.length ? (sorting[0]?.desc ? 'desc' : 'asc') : 'asc',
            }),
         );
         setIsLoading(false);
         // Close the modal
         onClose();
      } catch (error) {

      }
   };

   const onsavecontinue = async () => {
      setIsLoading(true);
      const processedData = {
         ...row,
         ordinal: Number(row.ordinal),
         brandid: Number(row.brandid),
         // ratepersalesid: Number(row.ratepersalesid),
         // rateperpurchaseid: Number(row.rateperpurchaseid)
      };

      try {
         if (isEditMode && currentRow && currentRow.id) {
            // Update existing item
            processedData.id = currentRow.id;
            await dispatch(updateSeries(processedData));
         } else {
            // Add new item
            await dispatch(addSeries(processedData));
         }
         dispatch(
            getSeries({
               page: currentPage,
               // limit: limit === 'ALL' ? undefined : limit,
               search: debouncedSearchQuery,
               sortBy: sorting.length ? sorting[0]?.name : 'name',
               sortOrder: sorting.length ? (sorting[0]?.desc ? 'desc' : 'asc') : 'asc',
            }),
         );
         setIsLoading(false);
         // Close the modal
      } catch (error) {

      }
   };

   const handleSubmit = async () => {
      const isValid = await validateStep();
      if (isValid) {
         onFormSubmit(); // Call the form submit logic if validation is successful
      } else {
         if (!row.name || row.name.trim() === '') {
            setTimeout(() => {
               nameInputRef.current?.focus(); // Focus the name input
            }, 100);
            return;
         } else if (!row.ordinal || row.ordinal === '') {
            setTimeout(() => {
               ordinalRef.current?.focus(); // ✅ Focus dropdown
            }, 100);
            return;
         } else if (!row.brandid || row.brandid === '') {
            setTimeout(() => {
               brandidRef.current?.focus(); // ✅ Focus dropdown
            }, 100);
            return;
         }
         toast({
            title: 'Validation Error',
            description: 'Please correct the errors before saving.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   const handlesavecontinue = async () => {
      const isValid = await validateStep();
      if (isValid) {
         onsavecontinue(); // Call the form submit logic if validation is successful
      } else {
         if (!row.name || row.name.trim() === '') {
            setTimeout(() => {
               nameInputRef.current?.focus(); // Focus the name input
            }, 100);
            return;
         } else if (!row.ordinal || row.ordinal === '') {
            setTimeout(() => {
               ordinalRef.current?.focus(); // ✅ Focus dropdown
            }, 100);
            return;
         } else if (!row.brandid || row.brandid === '') {
            setTimeout(() => {
               brandidRef.current?.focus(); // ✅ Focus dropdown
            }, 100);
            return;
         }
         toast({
            title: 'Validation Error',
            description: 'Please correct the errors before saving.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };
   useEffect(() => {
      const handleKeyDown = (e) => {
         const activeElement = document.activeElement;
         const isTyping =
            activeElement?.tagName === 'INPUT' ||
            activeElement?.tagName === 'TEXTAREA' ||
            activeElement?.getAttribute('role') === 'textbox' || // for react-select input
            activeElement?.isContentEditable;

         if (isTyping) return; // Don't trigger shortcuts while typing

         if (isOpen && e.key.toLowerCase() === 's') {
            e.preventDefault();

            if (e.shiftKey) {
               handlesavecontinue(); // SHIFT + S --- save & continue
            } else {
               handleSubmit(); // S --- Save & Finish
            }
         }
         if (isOpen && e.key.toLowerCase() === 'c') {
            handleCansel(); // c --- cansel
         }

      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
         window.removeEventListener('keydown', handleKeyDown);
      };
   }, [validateStep]);

   const nameInputRef = useRef();
   const companyRef = useRef();
   const ordinalRef = useRef();
   const brandidRef = useRef();
   const saveFinishRef = useRef();
   const cancelRef = useRef();
   const savecontinueRef = useRef();

   const handleCansel = async () => {
      onClose();
      if (!isEditMode) {
         clearFormData();
      }
   };

   useEffect(() => {
      if (demoname && row.name === '') {
         setRow((prev) => ({ ...prev, name: demoname }));
      }
      demoname = '';
   }, [demoname, row.name]);

   useEffect(() => {
      setTimeout(() => {
         nameInputRef.current?.focus();
      }, 100); // small delay sometimes helps
   }, [isOpen]);

   const handleCompanyChange = useCallback((selectedOptions) => {
      setRow((prev) => ({
         ...prev,
         compIds: selectedOptions ?? [],
      }));
   }, []);

   const dbName = company?.dbName;

   const companyOptions = useMemo(() => {
      return getShareCompanyOptions({ dbName, company: companyList });
   }, [companyList, dbName]);

   const lastcompanyOptions = useMemo(
      () =>
         companyOptions.map((company) => ({
            value: company.value,
            label: company.label,
            id: company.value,
            name: company.label,
         })),
      [companyOptions],
   );

   useEffect(() => {
      const handleEsc = (e) => {
         if (e.key === 'Escape') {
            e.preventDefault();
            handleCansel();
         }
      };

      document.addEventListener('keydown', handleEsc);
      return () => {
         document.removeEventListener('keydown', handleEsc);
      };
   }, [handleCansel]);

   return (
      <div className={`h-90vh flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
         <Modal isOpen={isOpen} onClose={handleCansel} size="3xl">
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
            <ModalContent bg="transparent" boxShadow="none" mx="15px" mt={{ base: '45%', md: '25%', xl: '15%' }}>
               <div
                  className={`rounded-2xl shadow-xl overflow-hidden
            ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}
          `}>
                  {/* Colored top border */}
                  <div className="h-1 bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-500" />

                  {/* Header */}
                  <ModalHeader className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                     <div className="flex items-center gap-3">
                        <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-blue-500 rounded-full" />
                        <div>
                           <h2 className={`text-xl font-semibold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                              {isEditMode ? 'Edit Series' : 'Add New Series'}
                           </h2>
                           <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {isEditMode ? 'Update series details' : 'Create a new series for your organization'}
                           </p>
                        </div>
                     </div>
                  </ModalHeader>

                  <ModalCloseButton
                     className={`top-5 right-5 rounded-md ${isDark
                        ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                     _focus={{
                        boxShadow: '0 0 0 2px rgba(99, 102, 241, 0.5)',
                        outline: 'none',
                     }}
                  />

                  {/* Body */}
                  <ModalBody className="px-6 py-5">
                     <Grid
                        templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)', xl: 'repeat(3, 1fr)' }}
                        gap={4}>
                        <GridItem className="seperator-width">
                           {renderDynamicDropdown(
                              'brandid',
                              'Brand',
                              brandData,
                              {
                                 value: row.brandid,
                                 onChange: (value) => {
                                    handleInputChange('brandid', value);
                                 },
                                 ref: brandidRef,
                                 showCustomNoOptions: true,
                                 openfrom: 'brand',
                                 onKeyDown: (e) => {
                                    if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
                                       e.preventDefault();
                                       nameInputRef.current?.focus();
                                    } else if (e.key === 'Escape') {
                                       onClose();
                                       if (!isEditMode) {
                                          clearFormData();
                                       }
                                    }
                                 },
                              },
                              errors,
                              textColor,
                              true,
                              'Select Brand',
                           )}
                        </GridItem>

                        <GridItem className="seperator-width">
                           {DynamicInput(
                              'name',
                              'Name',
                              {
                                 value: row.name,
                                 onChange: (e) => handleInputChange('name', e.target.value),
                                 ref: nameInputRef,
                                 placeholder: 'Enter series name',
                                 onKeyDown: (e) => {
                                    if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
                                       e.preventDefault();
                                       ordinalRef.current?.focus();
                                    }
                                 },
                              },
                              errors,
                              textColor,
                              'text',
                              true,
                           )}
                        </GridItem>

                        <GridItem className="seperator-width">
                           {DynamicInput(
                              'ordinal',
                              'Ordinal',
                              {
                                 value: row.ordinal,
                                 onChange: (e) => handleInputChange('ordinal', e.target.value),
                                 ref: ordinalRef,
                                 placeholder: 'Enter order',
                                 onKeyDown: (e) => {
                                    if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
                                       e.preventDefault();
                                       if (shouldSendCompanyidSeries) {
                                          companyRef.current?.focus();
                                       } else {
                                          saveFinishRef.current?.focus();
                                       }
                                    }
                                 },
                              },
                              errors,
                              textColor,
                              'text',
                              true,
                           )}
                        </GridItem>

                        {shouldSendCompanyidSeries && (
                           <GridItem className="seperator-width">
                              {renderDynamicUiDropdown(
                                 'company',
                                 'Company',
                                 lastcompanyOptions,
                                 {
                                    ref: companyRef,
                                    value: row.compIds,
                                    onChange: handleCompanyChange,
                                    onKeyDown: (e) => {
                                       if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
                                          e.preventDefault();
                                          saveFinishRef.current?.focus();
                                       }
                                    },
                                 },
                                 {},
                                 textColor,
                                 false,
                                 'Select company',
                                 true,
                                 undefined,
                                 true,
                              )}
                           </GridItem>
                        )}
                     </Grid>
                  </ModalBody>

                  {/* Footer */}
                  <ModalFooter
                     className={`px-6 py-4 border-t flex flex-wrap gap-3 justify-end ${isDark
                        ? 'border-gray-700/50 bg-gradient-to-r from-gray-800/50 to-gray-900/50'
                        : 'border-gray-200 bg-gradient-to-r from-gray-50 to-white'
                        }`}>
                     {!isEditMode && saveandcontinue === true && (
                        <Button
                           ref={savecontinueRef}
                           onClick={handlesavecontinue}
                           isLoading={isLoading}
                           disabled={isLoading}
                           size="sm"
                           className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5
        ${isDark
                                 ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 text-white hover:from-emerald-500 hover:to-emerald-400 border border-emerald-400/50'
                                 : 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 border-2 border-emerald-400/30'
                              }
      `}
                           _focus={{
                              boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.4)',
                              outline: 'none',
                           }}
                           onKeyDown={(e) => {
                              if (e.key === 'ArrowRight') {
                                 e.preventDefault();
                                 saveFinishRef.current?.focus();
                              }
                           }}>
                           <span className="flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                 />
                              </svg>
                              Save & Continue
                           </span>
                        </Button>
                     )}

                     <Button
                        onClick={handleSubmit}
                        isLoading={isLoading}
                        disabled={isLoading}
                        ref={saveFinishRef}
                        size="sm"
                        className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5
      ${isDark
                              ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-500 hover:to-blue-400 border border-blue-400/50'
                              : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 border-2 border-blue-400/30'
                           }
    `}
                        _focus={{
                           boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.4)',
                           outline: 'none',
                        }}
                        onKeyDown={(e) => {
                           if (e.key === 'ArrowRight') {
                              e.preventDefault();
                              cancelRef.current?.focus();
                           } else if (e.key === 'ArrowLeft') {
                              e.preventDefault();
                              if (!isEditMode) {
                                 savecontinueRef.current?.focus();
                              }
                           }
                        }}>
                        <span className="flex items-center gap-2">
                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 strokeWidth={2}
                                 d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                           </svg>
                           Save & Finish
                        </span>
                     </Button>

                     <Button
                        ref={cancelRef}
                        onClick={handleCansel}
                        size="sm"
                        className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5
      ${isDark
                              ? 'bg-gradient-to-r from-red-600/80 to-red-500/80 text-white hover:from-red-500 hover:to-red-400 border border-red-400/50'
                              : 'bg-white text-red-600 hover:bg-red-50 border-2 border-red-300 hover:border-red-400'
                           }
    `}
                        _focus={{
                           boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.3)',
                           outline: 'none',
                        }}
                        onKeyDown={(e) => {
                           if (e.key === 'ArrowLeft') {
                              e.preventDefault();
                              saveFinishRef.current?.focus();
                           }
                        }}>
                        <span className="flex items-center gap-2">
                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 strokeWidth={2}
                                 d="M6 18L18 6M6 6l12 12"
                              />
                           </svg>
                           Cancel
                        </span>
                     </Button>
                  </ModalFooter>
               </div>
            </ModalContent>
         </Modal>
      </div>
   );
};

export default SeriesMasterForm;
